import React, { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import cx from 'classnames'
import styles from './Button.module.scss'
import { ButtonProps, ClickEvt, MenuDropDownOption, ModalProps } from '~types'
import { getFevoWidgetNameFromUrl, isExternalLink, openFevoWidget } from 'utils'
import Link from 'next/link'
import { fireAnalyticsEvent, getGtmClickSource } from '~analyticsUtils'
import { useModal } from 'utils/hooks/useModal'
import { ConditionalWrapper, DynamicIcon, Modal } from '~elements'
import { useDropdown } from 'utils/hooks/useDropDown'
import DropDownMenu from '../DropDown/DropDownMenu'
import OutsideClickHandler from 'react-outside-click-handler'

const Button = ({
  analytics,
  color = 'light',
  disabled = false,
  fill = 'solid',
  isFullWidth = false,
  isLink = false,
  isTargetBlank = false,
  justify = 'left',
  modal,
  onClick,
  size = 'md',
  title,
  url = null,
  icon = null,
  id,
  dropdownOptions
}: ButtonProps) => {
  const { isModalOpen, openModal, closeModal } = useModal()
  const { isModalOpen: isSelectedModalOpen, openModal: openSelectedModal, closeModal: closeSelectedModal } = useModal()
  const [selectedModal, setSelectedModal] = useState<Omit<ModalProps, 'handleModalClose'> | null>(null)
  const buttonRef = useRef<HTMLButtonElement | HTMLAnchorElement>(null)
  const router = useRouter()
  const fevoWidgetName = url ? getFevoWidgetNameFromUrl(url) : ''
  const hasIcon = icon && icon.iconName
  const hasDropdown = dropdownOptions && dropdownOptions.length > 0
  const dropdownId =
    title && typeof title === 'string'
      ? `button-dropdown-${title?.replace(/\s+/g, '-').toLowerCase()}`
      : 'button-dropdown'
  const menuId = `${dropdownId}-menu`

  const dropdown = useDropdown<MenuDropDownOption>({
    options: dropdownOptions || [],
    placeholder: 'Select option',
    onOptionSelect: (_, option) => {
      if (option.restaurantId || option.artistId) {
        const newQuery = { ...router.query }
        if (option.restaurantId) newQuery.restaurantId = option.restaurantId
        if (option.artistId) newQuery.artistId = option.artistId
        // Update URL without page reload using shallow routing
        router.push({ pathname: router.pathname, query: newQuery }, undefined, { shallow: true })
      }

      if (option.mediaAsset) {
        setSelectedModal({
          view: {
            componentName: 'ModalViewMedia',
            mediaAsset: option.mediaAsset
          },
          width: 'content-aware',
          backgroundColor: 'gray-900'
        })
        openSelectedModal()
      }

      dropdown.resetDropdown()
      // Analytics for dropdown selection
      if (analytics && !analytics.skipEvent) {
        fireAnalyticsEvent(option.label || null, router.asPath, {
          clickSource: getGtmClickSource(router.asPath),
          clickTracking: `${analytics.linkModule || 'button'}:${title}:${option.value}:dropdown`,
          clickType: 'dropdown',
          linkLocation: analytics.linkLocation,
          linkModule: analytics.linkModule,
          linkPosition: analytics.linkPosition,
          eventData: analytics.eventData
        })
      }
    },
    includeClearOption: false
  })

  const wrapperClassNames = cx([
    styles['btn-wrapper'],
    styles[`btn-wrapper--justify-contents-${justify}`],
    styles[isFullWidth && !isLink ? 'btn-wrapper--is-full-width' : '']
  ])

  // @TODO: refactor button props to use a tier of themes for styling
  const getTheme = () => {
    if (disabled) {
      return 'disabled'
    } else if (fill === 'hollow') {
      return 'tierrary'
    } else if (color === 'light') {
      return 'secondary'
    } else if (color === 'primary') {
      return 'primary'
    } else if (color === 'gold') {
      return 'primary-accent'
    }
    return 'tierrary'
  }

  const buttonClassNames = isLink
    ? styles['is-link']
    : cx(styles['btn'], styles[`btn--size-${size}`], styles[`btn--theme-${getTheme()}`], styles[`btn--fill-${fill}`], {
        [styles['btn--is-disabled']]: disabled,
        [styles['btn--is-full-width']]: isFullWidth && !isLink,
        [styles['btn--has-dropdown']]: hasDropdown,
        [styles['dropdown-open']]: dropdown.isOpen
      })

  const isExternal = url && isExternalLink(url)

  const sharedProps = {
    ...(id && { id }),
    className: buttonClassNames,
    onClick: (e: ClickEvt) => clickHandler(e, Boolean(modal)),
    ref: buttonRef as React.RefObject<HTMLButtonElement & HTMLAnchorElement>
  }

  const analyticsClickHandler = () => {
    // GTM Analytics event
    const {
      clickTracking = '',
      eventData,
      linkLocation = '',
      linkModule = '',
      linkPosition = '',
      skipEvent = false
    } = analytics || {}

    const { eventCategory, eventDate, eventType, ticketmasterEventId } = eventData || {}

    if (!skipEvent) {
      fireAnalyticsEvent(url, router.asPath, {
        clickSource: getGtmClickSource(router.asPath),
        clickTracking: clickTracking ? clickTracking : `${linkModule}:${title}:cta`,
        clickType: 'cta',
        linkLocation: linkLocation,
        linkModule: linkModule,
        linkPosition: linkPosition,
        eventData: {
          eventDate: eventDate,
          eventType: eventType,
          eventCategory: eventCategory,
          ticketmasterEventId: ticketmasterEventId
        }
      })
    }
  }

  const clickHandler = (e: ClickEvt, hasModal: boolean) => {
    if (disabled) return false
    // If button has dropdown, toggle dropdown instead of normal click
    if (hasDropdown) {
      dropdown.handleToggle()
      return
    }
    if (onClick) onClick(e)

    if (hasModal) {
      openModal()
      //Add query params just for menu options
      if (id?.includes('-location')) return
      const newQuery = { ...router.query }
      if (modal && modal.id) newQuery.artistId = modal.id
      if (id && id.includes('-restaurant')) newQuery.restaurantId = id
      router.push({ pathname: router.pathname, query: newQuery }, undefined, { shallow: true })
    }

    if (fevoWidgetName) openFevoWidget(fevoWidgetName, e)
    analyticsClickHandler()
  }

  const handleSelectedModalClose = () => {
    closeSelectedModal()
    setSelectedModal(null)
    clearQueryParams()
  }

  const handleDefaultModalClose = () => {
    closeModal()
    clearQueryParams()
  }

  const clearQueryParams = () => {
    const newQuery = { ...router.query }
    delete newQuery.restaurantId
    delete newQuery.artistId
    router.push({ pathname: router.pathname, query: newQuery }, undefined, { shallow: true })
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (router.query.restaurantId && router.query.artistId && dropdownOptions && dropdownOptions.length > 0) {
      // Find the matching dropdown option based on restaurantId and menuId
      const matchingOption = dropdownOptions.find(
        option => option.restaurantId === router.query.restaurantId && option.artistId === router.query.artistId
      )

      if (!matchingOption || !matchingOption.mediaAsset) return

      setSelectedModal({
        view: {
          componentName: 'ModalViewMedia',
          mediaAsset: matchingOption.mediaAsset
        },
        width: 'content-aware',
        backgroundColor: 'gray-900'
      })
      openSelectedModal()
    }

    // Handle case for single menu (no dropdown)
    else if (router.query.restaurantId && router.query.artistId && modal && modal.id === router.query.artistId) {
      // For single menu items, open the modal directly if the IDs match
      openModal()
    }
  }, [router.query.restaurantId, router.query.artistId])

  useEffect(() => {
    let scrollTimer: ReturnType<typeof setTimeout>

    // Scroll when either modal opens due to query params
    if ((isModalOpen || isSelectedModalOpen) && router.query.restaurantId && router.query.artistId) {
      scrollTimer = setTimeout(() => {
        buttonRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        })
      }, 500)
    }

    return () => clearTimeout(scrollTimer)
  }, [isModalOpen, isSelectedModalOpen, router.query.restaurantId, router.query.artistId])

  const ButtonChildren = (
    <span>
      {title && title}
      {hasIcon && <DynamicIcon {...icon} name={icon.iconName} />}
    </span>
  )

  const renderButton = () => {
    if (url && !fevoWidgetName && !disabled) {
      if (isTargetBlank || isExternal) {
        return (
          <a
            {...sharedProps}
            href={url}
            {...(isExternal && { rel: 'noreferrer' })}
            {...(isTargetBlank && { target: '_blank' })}
          >
            {ButtonChildren}
          </a>
        )
      } else {
        return (
          <Link legacyBehavior href={url}>
            <a {...sharedProps} href={url}>
              {ButtonChildren}
            </a>
          </Link>
        )
      }
    } else if (fevoWidgetName || onClick || disabled || modal || hasDropdown) {
      return (
        <button {...sharedProps} {...(disabled && { disabled })}>
          {ButtonChildren}
        </button>
      )
    }
    return null
  }

  return (
    <ConditionalWrapper condition={!isLink} wrapper={children => <div className={wrapperClassNames}>{children}</div>}>
      <>
        <OutsideClickHandler onOutsideClick={dropdown.closeDropdown}>
          <div {...(hasDropdown && { onKeyDown: dropdown.handleKeyDown, ref: dropdown.dropdownRef })}>
            {renderButton()}
            <div className={styles.dropDownMenuContainer}>
              {hasDropdown && dropdown.isOpen && (
                <DropDownMenu
                  menuItems={dropdown.menuItems}
                  selectedValue={dropdown.selectedValue}
                  focusedIndex={dropdown.focusedIndex}
                  menuId={menuId}
                  dropdownId={dropdownId}
                  onOptionClick={dropdown.handleOptionClick}
                  onOptionKeyDown={dropdown.handleOptionKeyDown}
                  setFocusedIndex={dropdown.setFocusedIndex}
                  hasButtonStyles
                  parentRef={buttonRef}
                />
              )}
            </div>
          </div>
        </OutsideClickHandler>
        {selectedModal && (
          <Modal {...selectedModal} isOpen={isSelectedModalOpen} handleModalClose={handleSelectedModalClose} />
        )}
        {modal && <Modal {...modal} isOpen={isModalOpen} handleModalClose={handleDefaultModalClose} />}
      </>
    </ConditionalWrapper>
  )
}

export default Button
